import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  type ChartOptions,
  type ChartData,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { type ManagerDashboardChartDto } from '../core/_models';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

dayjs.locale('pt-br');

type Props = {
  data?: ManagerDashboardChartDto;
  height?: number;
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const ManagerBillsChart = ({ data, height = 350 }: Props) => {
  if (!data || !data.months.length) {
    return (
      <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: `${height}px` }}>
        <div className="text-center">
          <i className="bi bi-bar-chart fs-1 mb-3" />
          <p className="mb-0">Nenhum dado disponível para o período</p>
        </div>
      </div>
    );
  }

  const labels = data.months.map((m) => capitalize(dayjs(m, 'YYYY-MM').format('MMM/YY')));

  const payColor = '#F1416C';
  const receiveColor = '#3E97FF';

  const chartData: ChartData<'bar', number[], string> = {
    labels,
    datasets: [
      {
        label: 'A Pagar',
        data: data.bills_to_pay,
        backgroundColor: payColor,
        borderColor: payColor,
        borderWidth: 0,
        borderRadius: 4,
        barThickness: 'flex',
        maxBarThickness: 40,
        categoryPercentage: 0.6,
        barPercentage: 0.7,
      },
      {
        label: 'A Receber',
        data: data.bills_to_receive,
        backgroundColor: receiveColor,
        borderColor: receiveColor,
        borderWidth: 0,
        borderRadius: 4,
        barThickness: 'flex',
        maxBarThickness: 40,
        categoryPercentage: 0.6,
        barPercentage: 0.7,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: { display: false },
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y ?? 0;
            const label = context.dataset.label;
            return `${label}: R$ ${value.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11, weight: 400 }, color: '#7E8299', maxRotation: 0 },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(126, 130, 153, 0.1)' },
        ticks: {
          font: { size: 11, weight: 400 },
          color: '#7E8299',
          callback: (value) => {
            const numValue = Number(value);
            if (numValue === 0) return '0';
            const absValue = Math.abs(numValue);
            if (absValue >= 1000) {
              return `${(absValue / 1000).toFixed(0)}K`;
            }
            return numValue.toString();
          },
        },
        border: { display: false },
      },
    },
    animation: { duration: 1000, easing: 'easeInOutQuart' },
    interaction: { intersect: false, mode: 'index' },
  };

  return (
    <div>
      <div style={{ height: `${height}px` }}>
        <Bar data={chartData} options={options} />
      </div>
      <div className="mt-4 d-flex justify-content-center">
        <div className="d-flex flex-row gap-6">
          <div className="d-flex align-items-center gap-1">
            <div
              className="rounded-circle"
              style={{ width: '12px', height: '12px', backgroundColor: payColor, flexShrink: 0 }}
            />
            <span className="text-gray-700">A Pagar</span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <div
              className="rounded-circle"
              style={{ width: '12px', height: '12px', backgroundColor: receiveColor, flexShrink: 0 }}
            />
            <span className="text-gray-700">A Receber</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerBillsChart;
